import { Box, InputAdornment, SvgIcon, TextField, Typography, styled as muiStyled } from '@mui/material';
import Autocomplete, {
    AutocompleteProps,
    AutocompleteRenderInputParams,
    AutocompleteRenderOptionState,
} from '@mui/material/Autocomplete';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FieldError } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { withIconStyles } from 'shared';
import styled from 'styled-components';

import { findCities, findPlaces, getVenueByGooglePlaceId } from 'api';
import { QueryParamEnum } from 'constants/queryParams';
import LocationPinSvg from 'images/icons/location_pin.svg?react';
import { EColors, getColor } from 'theme';
import { Copy } from 'ui';
import CheckMarkSvg from '../../../images/icons/check-mark.svg?react';
import MapPinIcon from '../svg/MapPinIcon';

// Create a base component with proper typing
const BaseAutocomplete = (props: AutocompleteProps<TOption, boolean, boolean, boolean>) => <Autocomplete {...props} />;

type SizeType = 'small' | 'medium';

const StyledAutocomplete = muiStyled(BaseAutocomplete, {
    shouldForwardProp: prop => !['hideColors', 'size', 'hasError'].includes(prop as string),
})<{ hideColors?: boolean; size?: SizeType; hasError?: boolean }>(({ hideColors, size = 'medium', hasError }) => {
    const fontSize = size === 'small' ? '.75rem' : '.875rem';
    const paddingLeft = size === 'small' ? '14px' : '17px';
    const inputHeight = size === 'small' ? '40px' : '48px';
    const labelTransform = size === 'small' ? 'translate(16px, 12px)' : 'translate(16px, 14px)';

    return {
        flex: 1,
        boxSizing: 'border-box',
        width: '100%',
        '& .MuiInputBase-root.MuiInputBase-root': {
            height: inputHeight,
            fontSize,
            borderRadius: '12px',
            color: getColor(EColors.pureBlack),
            backgroundColor: getColor(EColors.v3Background),
            paddingLeft,
            '& > .MuiOutlinedInput-notchedOutline': {
                border: hasError
                    ? `1px solid ${getColor(EColors.red)}`
                    : hideColors
                      ? 'none'
                      : `1px solid ${getColor(EColors.v3Border)}`,
                borderRadius: '12px',
            },
            // Keep red border on focus/hover when there's an error (override MUI theme)
            ...(hasError && {
                '&.Mui-focused > .MuiOutlinedInput-notchedOutline': {
                    borderColor: `${getColor(EColors.red)} !important`,
                    borderWidth: '1px !important',
                },
                '&:hover > .MuiOutlinedInput-notchedOutline': {
                    borderColor: `${getColor(EColors.red)} !important`,
                },
            }),
            '& > input': {
                padding: '0px',
            },
        },
        '& .MuiFormLabel-root': {
            transform: labelTransform,
            fontSize,
            color: getColor(EColors.pureBlack, 0.5),
        },
        '& .MuiFormLabel-root.MuiInputLabel-shrink': {
            color: getColor(EColors.pureBlack, 0.6),
            fontSize,
            transform: 'translate(9px, -50%) scale(1)',
            backgroundColor: getColor(EColors.pureWhite),
            padding: '0 5px',
            lineHeight: '1em',
        },
        '&:hover .MuiInputBase-root:hover > .MuiOutlinedInput-notchedOutline': {
            borderColor: getColor(EColors.v3Dark),
        },
    };
});

const LocationPinIcon = styled(withIconStyles(LocationPinSvg))`
    min-width: 21px;
    max-width: 21px;
    padding-right: 1px;
`;

const CheckMarkIcon = styled(CheckMarkSvg)`
    margin-left: 12px;
`;

const SuggestionText = styled(Copy).attrs({ regular: true })<{ size?: SizeType }>(({ size = 'medium' }) => ({
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: size === 'small' ? '.75rem' : '.875rem',
    lineHeight: size === 'small' ? '1rem' : '1.375rem',
    flex: 1,
}));

type TOption = {
    description: string;
    placeId: string;
    noIcon?: boolean;
    types?: string[];
};

type TPlaceInput<TInputAsSuggestion extends boolean | undefined> = {
    locationBias?: Bizly.Location;
    clearable?: boolean;
    citiesOnly?: boolean;
    directVenueLinking?: boolean;
    locationValue?: string;
    placeId?: string;
    disabled?: boolean;
    label?: string;
    showLocationIcon?: boolean;
    hideColors?: boolean;
    size?: SizeType;
    error?: FieldError;
    placeholder?: string;
    required?: boolean;
    onRemove?: () => void;
} & (TInputAsSuggestion extends true
    ? {
          inputAsSuggestion: true;
          onChange: (city: string, googlePlaceId?: string | undefined) => void;
      }
    : {
          inputAsSuggestion?: false | undefined;
          onChange: (city: string, googlePlaceId: string) => void;
      });

export const GooglePlacesAutocomplete = (props: TPlaceInput<true> | TPlaceInput<false>) => {
    const {
        locationBias,
        clearable,
        citiesOnly,
        directVenueLinking,
        inputAsSuggestion,
        locationValue,
        placeId,
        disabled,
        label,
        placeholder,
        required,
        onRemove,
        onChange,
        showLocationIcon = false,
        hideColors = false,
        size = 'medium',
        error,
    } = props;

    const navigate = useNavigate();
    const location = useLocation();

    const [inputQuery, setInputQuery] = useState<string>(locationValue ?? '');
    const [options, setOptions] = useState<TOption[]>([]);
    const [loading, setLoading] = useState(false);
    const [noOptionsText, setNoOptionsText] = useState(
        `We couldn't find any locations matching "${inputQuery ?? ''}". Try again.`
    );
    const [lastValidLocation, setLastValidLocation] = useState<string | null>(locationValue || null);
    const selectedPlaceIdsRef = useRef<Set<string>>(new Set());

    const inputOption = useMemo(
        () => ({
            description: locationValue ?? '',
            placeId: placeId ?? '',
            noIcon: true,
            types: undefined,
        }),
        [locationValue, placeId]
    );

    // Sync inputQuery with locationValue when it changes externally
    useEffect(() => {
        const safeLocationValue = locationValue ?? '';
        if (safeLocationValue !== inputQuery) {
            setInputQuery(safeLocationValue);
            setLastValidLocation(safeLocationValue || null);
        }
    }, [locationValue]); // eslint-disable-line react-hooks/exhaustive-deps

    // Sort predictions to prioritize exact matches
    const sortPredictions = useCallback((predictions: TOption[], query: string) => {
        return [...predictions].sort((a, b) => {
            const aIsExactMatch = a.description.toLowerCase() === query.toLowerCase();
            const bIsExactMatch = b.description.toLowerCase() === query.toLowerCase();

            if (aIsExactMatch && !bIsExactMatch) return -1;
            if (!aIsExactMatch && bIsExactMatch) return 1;
            return 0;
        });
    }, []);

    // Debounce the API call to avoid excessive requests
    const debouncedFetch = useMemo(
        () =>
            debounce(async (query: string) => {
                setNoOptionsText(`We couldn't find any locations matching "${query || ''}". Try again.`);
                if (query.length > 0) {
                    setLoading(true);
                    try {
                        const response = citiesOnly
                            ? await findCities(query)
                            : await findPlaces(query, locationBias?.lat, locationBias?.lng);
                        if (citiesOnly ? response.status === 'OK' : response.success) {
                            // Filter out already selected place IDs and sort results
                            const filteredPredictions = response.predictions.filter(
                                (prediction: TOption) => !selectedPlaceIdsRef.current.has(prediction.placeId)
                            );
                            const sortedPredictions = sortPredictions(filteredPredictions, query);
                            setOptions([...sortedPredictions]);
                        } else {
                            setLoading(false);
                            setOptions([]);
                        }
                    } catch {
                        setNoOptionsText('Something went wrong. Please try again.');
                        setLoading(false);
                        setOptions([]);
                    } finally {
                        setLoading(false);
                    }
                } else {
                    setLoading(false);
                    setOptions([]);
                }
            }, 300),
        [locationBias, citiesOnly, sortPredictions]
    );

    useEffect(() => {
        // Call the debounced function whenever the input value changes
        debouncedFetch(inputQuery);

        // Cleanup function to cancel any pending debounced calls on component unmount
        return () => {
            debouncedFetch.cancel();
        };
    }, [inputQuery, debouncedFetch]);

    const handleSelection = useCallback(
        async (city: string, placeId: string) => {
            setInputQuery(city);
            setLastValidLocation(city);
            onChange?.(city, placeId);

            if (directVenueLinking && placeId) {
                try {
                    // Mark this place ID as selected to remove it from future search results
                    selectedPlaceIdsRef.current.add(placeId);

                    // Get venue by Google Place ID
                    const result = await getVenueByGooglePlaceId(placeId);
                    if (result.success) {
                        const params = new URLSearchParams(location.search);
                        params.set(QueryParamEnum.VENUE_ID, result.venue.id.toString());
                        navigate({
                            pathname: location.pathname,
                            search: params.toString(),
                        });
                    }
                } catch (error) {
                    console.error('Error fetching venue details:', error);
                }
            }
        },
        [directVenueLinking, onChange, location.search, location.pathname, navigate]
    );

    const handleClear = useCallback(() => {
        setLoading(false);
        setOptions([]);
        selectedPlaceIdsRef.current.clear();
        if (clearable) onRemove?.();
    }, [clearable, onRemove]);

    const onInputChange = useCallback(
        (_: React.SyntheticEvent, newInputQuery: string, reason: string) => {
            setInputQuery(newInputQuery);

            // Only clear googlePlaceId when the user is typing. When reason is 'reset' (after
            // selecting an option), MUI fires this after the selection — do not overwrite the
            // placeId that was just set by onSelectionChange.
            if (reason === 'input') {
                onChange?.(newInputQuery, '');
            }

            if (!newInputQuery) {
                handleClear();
            }
        },
        [handleClear, onChange]
    );

    const onSelectionChange = useCallback(
        (e: React.SyntheticEvent, val: NonNullable<string | TOption> | (string | TOption)[] | null) => {
            if (!e || !val || typeof val === 'string' || Array.isArray(val)) {
                handleClear();
                return;
            }
            handleSelection(val.description, val.placeId);
        },
        [handleClear, handleSelection]
    );

    const renderOption = useCallback(
        (props: React.HTMLAttributes<HTMLLIElement>, option: TOption, state: AutocompleteRenderOptionState) => {
            return (
                <li {...props} key={option.placeId}>
                    <Box display="flex" alignItems="center" justifyContent="center" flex={1}>
                        <Box display="flex" alignItems="center" flex={1}>
                            {!option.noIcon && <LocationPinIcon />}
                            {<SuggestionText size={size}>{option.description}</SuggestionText>}
                        </Box>
                        {state.selected && <CheckMarkIcon />}
                    </Box>
                </li>
            );
        },
        []
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === 'Enter') {
                if (options.length > 0) {
                    setLastValidLocation(options[0].description);
                    setInputQuery(options[0].description);
                    onChange?.(options[0].description, options[0].placeId);
                } else if (inputQuery && inputQuery !== lastValidLocation) {
                    setInputQuery(lastValidLocation ?? locationValue ?? '');
                    onChange?.(lastValidLocation ?? locationValue ?? '', placeId || '');
                }
            }
        },
        [options, inputQuery, lastValidLocation, locationValue, placeId, onChange]
    );

    return (
        <Box>
            <StyledAutocomplete
                freeSolo
                disabled={disabled}
                loading={loading}
                disableClearable={!clearable}
                options={options.length > 0 ? [...(inputAsSuggestion ? [inputOption] : []), ...options] : []}
                getOptionLabel={(option: TOption | string) =>
                    typeof option === 'string' ? option : option.description
                }
                renderOption={renderOption}
                noOptionsText={noOptionsText}
                inputValue={inputQuery}
                onInputChange={onInputChange}
                value={inputOption}
                filterOptions={(x: TOption[]) => x}
                renderInput={(params: AutocompleteRenderInputParams) => (
                    <TextField
                        {...params}
                        label={label}
                        placeholder={placeholder || label}
                        variant="outlined"
                        fullWidth
                        required={required}
                        InputProps={{
                            ...params.InputProps,
                            startAdornment: showLocationIcon ? (
                                <InputAdornment position="start">
                                    <SvgIcon
                                        viewBox="0 0 14 14"
                                        sx={{
                                            width: '20px',
                                            height: '20px',
                                        }}
                                    >
                                        <MapPinIcon strokeColor={getColor(EColors.pureBlack)} />
                                    </SvgIcon>
                                </InputAdornment>
                            ) : undefined,
                        }}
                        onKeyDown={handleKeyDown}
                    />
                )}
                onChange={onSelectionChange}
                hideColors={hideColors}
                size={size}
                hasError={!!error}
            />
            {error && (
                <Typography variant="body2" color={getColor(EColors.red)} fontWeight={500}>
                    {error.message ?? 'Something went wrong'}
                </Typography>
            )}
        </Box>
    );
};
